/**
 * Commands of the bots mentioned in the post being written, looked up once
 * per bot and kept for a while, so typing never waits for the server.
 */

import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { isAxiosError } from 'axios';

import { apiLookupBotCommands } from 'mastodon/api/bot_commands';
import { domain } from 'mastodon/initial_state';

import type { BotCommands } from './bot_commands';
import { mentionedUsernames } from './bot_commands';

const KEEP_FOR = 10 * 60 * 1000;
const RETRY_AFTER_ERROR = 60 * 1000;

interface Entry {
  commands: BotCommands | null;
  expiresAt: number;
  pending?: Promise<void>;
}

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let version = 0;

function notify() {
  version++;
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function load(username: string) {
  const current = entries.get(username);
  if (current?.pending) return current.pending;
  if (current && current.expiresAt > Date.now()) return Promise.resolve();

  const pending = apiLookupBotCommands(username).then(
    (data) => {
      entries.set(username, {
        commands:
          data.commands.length > 0
            ? { acct: data.acct, commands: data.commands }
            : null,
        expiresAt: Date.now() + KEEP_FOR,
      });
      notify();
    },
    (error: unknown) => {
      // 404: not a bot, or a bot without commands
      const notFound = isAxiosError(error) && error.response?.status === 404;

      entries.set(username, {
        commands: current?.commands ?? null,
        expiresAt: Date.now() + (notFound ? KEEP_FOR : RETRY_AFTER_ERROR),
      });
      notify();
    },
  );

  entries.set(username, {
    commands: current?.commands ?? null,
    expiresAt: current?.expiresAt ?? 0,
    pending,
  });

  return pending;
}

function known(usernames: string[]) {
  return usernames.flatMap((username) => {
    const commands = entries.get(username)?.commands;
    return commands ? [commands] : [];
  });
}

/** Commands of the bots mentioned in `text`, loading any that are missing */
export async function loadBotCommandsFor(text: string) {
  const usernames = mentionedUsernames(text, domain);
  await Promise.all(usernames.map(load));

  return known(usernames);
}

/** The same for React, updating once the commands have arrived */
export function useBotCommandsFor(text: string) {
  const joined = mentionedUsernames(text, domain).join(' ');
  const currentVersion = useSyncExternalStore(subscribe, () => version);

  useEffect(() => {
    if (joined) joined.split(' ').forEach((username) => void load(username));
  }, [joined]);

  return useMemo(
    () => (joined ? known(joined.split(' ')) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed when the store changes
    [joined, currentVersion],
  );
}

export function resetBotCommandsForTests() {
  entries.clear();
  version = 0;
}
